<template>
    <button
    class="button-list"
    :disabled="disabled"
    :class="[{'selected': selected}, {'disabled': disabled}, variant]"
    @click.stop="emit('clicked')">
        <!-- Button Icon -->
        <div
        v-if="icon"
        class="icon-wrapper"
        :class="{
            'with-background-color': !!iconBackgroundColor,
            'light-background': iconBackgroundColor === 'var(--secondary-light-2)'
        }"
        :style="`background-color: ${iconBackgroundColor}`">
            <app-icon class="left-icon"/>
        </div>
        <!-- Button label -->
        <div class="label">{{ label }}</div>
        <!-- Check Icon -->
        <check-icon v-if="selected && variant !== 'negative'" class="check-icon" />
    </button>
</template>

<script setup>
import CheckIcon from '@/assets/icons/check-line.svg?inline'
/* Vue */
import { computed, defineAsyncComponent } from 'vue'
/* Emmitters */
const emit = defineEmits(['clicked'])

/** Props received:
@label {String}
@icon {String}
@variant {String} can be [primary (default), secondary, tertiary, negative]
@selected {Boolean}
@disabled {Boolean}
@iconBackgroundColor { String }
*/
const props = defineProps({
    label: String,
	icon: String,
    variant: {
        type: String,
        default: 'primary'
    },
    selected: Boolean,
    disabled: Boolean,
    iconBackgroundColor: String
})

/* Computed */
const appIcon = computed(() => {
    const icon = props.icon
	return defineAsyncComponent(() => import('@/assets/icons/' + icon + '.svg?inline'))
})
</script>

<style lang="scss" scoped>
.button-list {
    width: max-content;
    height: 40px;
    padding: 0 16px;

    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    @include H7-Medium;
    color: var(--secondary-dark-2);
    line-height: 40px;

    background-color: var(--secondary-light-4);
    cursor: pointer;

    .icon-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
		flex-shrink: 0;

        svg.left-icon {
            width: 16px;
            height: 16px;

            flex-shrink: 0;
        }

        &.with-background-color {
            width: 24px;
            height: 24px;

            border-radius: 8px;

            svg.left-icon {
                :deep(.change-color-fill) {
                    fill: var(--secondary-light-4);
                }
            }

            &.light-background {
                svg.left-icon {
                    :deep(.change-color-fill) {
                        fill: var(--secondary);
                    }
                }
            }
        }
    }

    .label {
        height: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    /* Variant Classes */
    &.primary {
        height: 48px;
        line-height: 48px;

        svg.left-icon {
            :deep(.change-color-fill) {
                fill: var(--primary);
            }
        }
    }

    &.secondary {
        svg.left-icon {
            :deep(.change-color-fill) {
                fill: var(--secondary);
            }
        }
    }

    &.tertiary {
        @include H6-Regular;
        color: var(--secondary-dark-3);
    }

    &.negative {
        color: var(--negative);

        svg.left-icon {
            :deep(.change-color-fill) {
                fill: var(--negative);
            }
        }
    }

    @media (hover: hover) and (pointer: fine) {
        &:hover {
            background-color: var(--secondary-light-2);
        }
    }

    &:active {
        background-color: var(--secondary-light-1);
    }

    &:not(.negative) {
        &.selected {
            .label {
                color: var(--primary);
            }

            svg.check-icon {
                flex-shrink: 0;
                margin-left: auto;

                :deep(.change-color-fill) {
                    fill: var(--primary);
                }
            }
        }
    }

    &.disabled {
        opacity: 0.24;
        pointer-events: none;
    }
}
</style>
